/**
 * Formatters and utilities for consistent data presentation
 */
import { format, formatDistanceToNow, isValid, parseISO } from "date-fns";

import { DateFormats } from "@/components/ui/DateDisplay";
import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";

/** Use this as format string to get relative time (e.g. "2 days ago"). Not a date-fns pattern. */
export const RELATIVE_FORMAT = "relative";

/**
 * Format a date consistently
 * @param date Date to format
 * @param formatString Date format string to use, or RELATIVE_FORMAT for relative time
 * @returns Formatted date string or fallback text
 */
function coerceToDate(date: string | Date): Date {
  if (typeof date === "string") return parseISO(date);
  if (date instanceof Date) return date;

  return new Date(date);
}

function applyDateFormat(dateObject: Date, formatString: string): string {
  if (formatString === RELATIVE_FORMAT) {
    return formatDistanceToNow(dateObject, { addSuffix: true });
  }

  return format(dateObject, formatString);
}

function publishDateFormatError(
  err: unknown,
  date: string | Date,
  formatString: string,
): void {
  const error = err instanceof Error ? err : new Error(String(err));

  void EventPublisher.publishError(
    EventKeys.SERVER_DATE_FORMATTING_ERROR,
    error,
    {
      date: String(date),
      formatString,
    },
  );
}

export function formatDate(
  date: string | Date | null | undefined,
  formatString: string = DateFormats.FULL,
  fallback = "No date",
): string {
  if (!date) return fallback;

  try {
    const dateObject = coerceToDate(date);

    if (!isValid(dateObject)) return fallback;

    return applyDateFormat(dateObject, formatString);
  } catch (err) {
    publishDateFormatError(err, date, formatString);

    return fallback;
  }
}

/**
 * Capitalizes a need urgency value, returning "Normal" for null/undefined.
 * e.g. "high" → "High", null → "Normal"
 */
export function getUrgencyLabel(urgency: string | null | undefined): string {
  if (!urgency) return "Normal";

  return urgency.charAt(0).toUpperCase() + urgency.slice(1);
}

/**
 * Returns the display name for a need category, falling back to "Uncategorized".
 */
export function getCategoryName(
  category: { name: string } | null | undefined,
): string {
  return category?.name ?? "Uncategorized";
}

/**
 * Returns the ISO string of a date, or null if the date is null/undefined.
 */
export function toIsoOrNull(date: Date | null | undefined): string | null {
  return date?.toISOString() ?? null;
}
